import type { ResumeAnalysis } from "./types";
import type { NormalizedResume } from "./normalizers";
import { BaseModule } from "./baseModule";
import type { AxiosInstance } from "axios";
import type { ErrorHandler } from "./errorHandler";
import type {
  Resume,
  ResumeUploadResponse,
  ResumeWithCandidate,
  DetailedResumeStatus,
  AdminResumeFilters,
  BulkResumeStatusUpdate,
  BulkOperationResult,
  RetryResumeProcessingRequest,
  ProgressCallback,
  FileUploadConfig,
} from "./types";
import { normalizeResume, normalizeResumeStatus } from "./normalizers";

/**
 * Resume Module - Handles all resume-related operations
 *
 * This module provides functionality for:
 * - Resume file uploads with progress tracking
 * - Resume processing status tracking
 * - Resume scanning and AI processing
 * - Resume download and asset management
 * - Admin resume management features
 */
export class ResumeModule extends BaseModule {
  // File upload constraints
  private static readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  private static readonly ALLOWED_TYPES = ["application/pdf"];

  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  // ============================================================================
  // User Resume Operations
  // ============================================================================

  /**
   * Upload a resume file with progress tracking
   *
   * @param file - The resume file to upload
   * @param userId - Optional user ID (for admin uploads)
   * @param label - Optional label for the resume
   * @param onProgress - Optional callback for upload progress
   * @returns Promise with upload response containing resume ID and status
   */
  async uploadResume(
    file: File,
    userId?: string,
    label?: string,
    onProgress?: ProgressCallback,
  ): Promise<ResumeUploadResponse> {
    // Validate file before upload
    this.validateFile(file);

    const formData = new FormData();
    formData.append("resume", file); // Backend expects 'resume' field name

    if (userId) {
      formData.append("user_id", userId);
    }

    if (label) {
      formData.append("label", label);
    }

    const config: FileUploadConfig = {
      // Don't set Content-Type header - let browser set it with boundary
      onProgress,
      maxFileSize: ResumeModule.MAX_FILE_SIZE,
      allowedTypes: ResumeModule.ALLOWED_TYPES,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(progress);
        }
      },
    };

    try {
      const response = await this.httpClient.post<ResumeUploadResponse>(
        "/api/resumes",
        formData,
        config,
      );
      return response.data;
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Get all resumes for the current user
   *
   * @returns Promise with array of user's resumes
   */
  async getUserResumes(): Promise<NormalizedResume[]> {
    const resumes = await this.get<Resume[]>("/api/user/resumes");
    return resumes.map(normalizeResume);
  }

  /**
   * Get a specific resume by ID
   *
   * @param id - Resume ID
   * @returns Promise with resume details
   */
  async getResume(id: string): Promise<Resume> {
    const resume = await this.get<Resume>(`/api/resumes/${id}`);
    return normalizeResume(resume);
  }

  /**
   * Delete a resume
   *
   * @param id - Resume ID
   * @returns Promise that resolves when deletion is complete
   */
  async deleteResume(id: string): Promise<void> {
    await this.delete<void>(`/api/resumes/${id}`);
  }

  /**
   * Update resume label
   *
   * @param id - Resume ID
   * @param label - New label for the resume
   * @returns Promise with updated resume
   */
  async updateResumeLabel(id: string, label: string): Promise<Resume> {
    return normalizeResume(
      await this.put<Resume>(`/api/resumes/${id}/label`, { label }),
    );
  }

  // ============================================================================
  // Resume Processing Operations
  // ============================================================================

  /**
   * Trigger AI scanning/processing for a resume
   *
   * @param id - Resume ID
   * @returns Promise with scan result including score and extracted data
   */
  async scanResume(id: string): Promise<{
    message: string;
    status: string;
    score: number;
    extracted_data?: ResumeAnalysis;
  }> {
    return this.post<{
      message: string;
      status: string;
      score: number;
      extracted_data?: ResumeAnalysis;
    }>(`/api/resumes/${id}/scan`);
  }

  /**
   * Get AI review data for a scanned resume
   *
   * @param id - Resume ID
   * @returns Promise with review data including extracted information
   */
  async getResumeReview(id: string): Promise<{
    id: string;
    status: string;
    score: number | null;
    ai_model: string;
    scanned_at: string | null;
    extracted_data: ResumeAnalysis | null;
    file_url: string;
    image_urls: string[];
  }> {
    return this.get<{
      id: string;
      status: string;
      score: number | null;
      ai_model: string;
      scanned_at: string | null;
      extracted_data: ResumeAnalysis | null;
      file_url: string;
      image_urls: string[];
    }>(`/api/resumes/${id}/review`);
  }

  /**
   * Propagate resume data to user profile
   *
   * @param id - Resume ID
   * @returns Promise with result of profile update
   */
  async propagateResumeToProfile(id: string): Promise<{
    message: string;
    updated_fields: string[];
  }> {
    return this.post<{
      message: string;
      updated_fields: string[];
    }>(`/api/resumes/${id}/propagate-profile`);
  }

  /**
   * Get detailed processing status for a resume
   *
   * @param id - Resume ID
   * @returns Promise with detailed status including progress and metadata
   */
  async getProcessingStatus(id: string): Promise<DetailedResumeStatus> {
    return normalizeResumeStatus(
      await this.get<DetailedResumeStatus>(`/api/resumes/${id}/status`),
    );
  }

  /**
   * Download a resume file as a blob
   *
   * @param id - Resume ID
   * @returns Promise with resume file as Blob
   */
  async downloadResume(id: string): Promise<Blob> {
    return this.downloadFile(`/api/resumes/${id}/download`);
  }

  /**
   * Retry processing for a failed or stuck resume
   *
   * @param id - Resume ID
   * @param request - Optional retry configuration
   * @returns Promise that resolves when retry is initiated
   */
  async retryProcessing(
    id: string,
    request?: RetryResumeProcessingRequest,
  ): Promise<void> {
    await this.post<void>(`/api/resumes/${id}/retry-processing`, request);
  }

  /**
   * Poll processing status until completion or failure
   *
   * @param id - Resume ID
   * @param onProgress - Optional callback for progress updates
   * @param pollInterval - Polling interval in milliseconds (default: 2000)
   * @param maxAttempts - Maximum polling attempts (default: 30)
   * @returns Promise with final processing status
   */
  async pollProcessingStatus(
    id: string,
    onProgress?: (status: DetailedResumeStatus) => void,
    pollInterval: number = 2000,
    maxAttempts: number = 30,
  ): Promise<DetailedResumeStatus> {
    let attempts = 0;

    while (attempts < maxAttempts) {
      const status = await this.getProcessingStatus(id);

      if (onProgress) {
        onProgress(status);
      }

      // Check if processing is complete (success or failure)
      if (status.status === "scanned" || status.status === "error") {
        return status;
      }

      // Wait before next poll
      await this.sleep(pollInterval);
      attempts++;
    }

    throw new Error("Processing status polling timed out");
  }

  /**
   * Check if resume processing is complete
   *
   * @param status - Resume processing status
   * @returns True if processing is complete (success or failure)
   */
  isProcessingComplete(status: DetailedResumeStatus): boolean {
    return status.status === "scanned" || status.status === "error";
  }

  /**
   * Check if resume processing failed
   *
   * @param status - Resume processing status
   * @returns True if processing failed
   */
  isProcessingFailed(status: DetailedResumeStatus): boolean {
    return status.status === "error";
  }

  /**
   * Check if resume processing is in progress
   *
   * @param status - Resume processing status
   * @returns True if processing is in progress
   */
  isProcessingInProgress(status: DetailedResumeStatus): boolean {
    return status.status === "processing";
  }

  /**
   * Get processing progress percentage
   *
   * @param status - Resume processing status
   * @returns Progress percentage (0-100)
   */
  getProcessingProgress(status: DetailedResumeStatus): number {
    return Math.max(0, Math.min(100, status.progress_percentage));
  }

  /**
   * Get estimated completion time for processing
   *
   * @param status - Resume processing status
   * @returns Estimated completion time as Date object, or null if not available
   */
  getEstimatedCompletionTime(status: DetailedResumeStatus): Date | null {
    if (status.estimated_completion_time) {
      return new Date(status.estimated_completion_time);
    }
    return null;
  }

  // ============================================================================
  // Admin Resume Operations
  // ============================================================================

  /**
   * Get all resumes with candidate information (admin only)
   *
   * @param filters - Optional filters for resume listing
   * @returns Promise with array of resumes with candidate details
   */
  async getAdminResumes(
    filters?: AdminResumeFilters,
  ): Promise<ResumeWithCandidate[]> {
    const queryString = filters ? this.buildQueryString(filters) : "";
    const endpoint = queryString
      ? `/api/admin/resumes?${queryString}`
      : "/api/admin/resumes";

    // Backend returns a flat array, not wrapped in an object
    const resumes = await this.get<ResumeWithCandidate[]>(endpoint);
    return resumes.map(normalizeResume);
  }

  /**
   * Bulk update resume status (admin only)
   *
   * @param request - Bulk update request with resume IDs and new status
   * @returns Promise with operation result including success/failure counts
   */
  async bulkUpdateResumeStatus(
    request: BulkResumeStatusUpdate,
  ): Promise<BulkOperationResult> {
    return this.post<BulkOperationResult>(
      "/api/admin/resumes/bulk-update-status",
      request,
    );
  }

  /**
   * Get resume analytics for admin dashboard
   *
   * @returns Promise with resume analytics data
   */
  async getResumeAnalytics(): Promise<{
    total_resumes: number;
    by_status: Record<string, number>;
    average_score: number;
    processing_success_rate: number;
  }> {
    // This would need a backend endpoint, but we can calculate from the list
    const resumes = await this.getAdminResumes();

    const analytics = {
      total_resumes: resumes.length,
      by_status: {} as Record<string, number>,
      average_score: 0,
      processing_success_rate: 0,
    };

    let totalScore = 0;
    let scoredResumes = 0;
    let successfulProcessing = 0;

    resumes.forEach((resume) => {
      // Count by status
      analytics.by_status[resume.status] =
        (analytics.by_status[resume.status] || 0) + 1;

      // Calculate average score
      if (resume.score !== undefined && resume.score !== null) {
        totalScore += resume.score;
        scoredResumes++;
      }

      // Count successful processing
      if (resume.status === "scanned") {
        successfulProcessing++;
      }
    });

    analytics.average_score =
      scoredResumes > 0 ? totalScore / scoredResumes : 0;
    analytics.processing_success_rate =
      resumes.length > 0 ? (successfulProcessing / resumes.length) * 100 : 0;

    return analytics;
  }

  /**
   * Get resumes by status (admin only)
   *
   * @param status - Resume status to filter by
   * @returns Promise with filtered resumes
   */
  async getResumesByStatus(status: string): Promise<ResumeWithCandidate[]> {
    return this.getAdminResumes({ status });
  }

  /**
   * Get resumes by candidate name (admin only)
   *
   * @param candidateName - Candidate name to search for
   * @returns Promise with filtered resumes
   */
  async getResumesByCandidateName(
    candidateName: string,
  ): Promise<ResumeWithCandidate[]> {
    return this.getAdminResumes({ candidate_name: candidateName });
  }

  /**
   * Get resumes by score range (admin only)
   *
   * @param minScore - Minimum score
   * @param maxScore - Maximum score
   * @returns Promise with filtered resumes
   */
  async getResumesByScoreRange(
    minScore: number,
    maxScore: number,
  ): Promise<ResumeWithCandidate[]> {
    return this.getAdminResumes({ score_min: minScore, score_max: maxScore });
  }

  /**
   * Get resumes by date range (admin only)
   *
   * @param dateFrom - Start date (ISO string)
   * @param dateTo - End date (ISO string)
   * @returns Promise with filtered resumes
   */
  async getResumesByDateRange(
    dateFrom: string,
    dateTo: string,
  ): Promise<ResumeWithCandidate[]> {
    return this.getAdminResumes({ date_from: dateFrom, date_to: dateTo });
  }

  /**
   * Get paginated resumes (admin only)
   *
   * @param page - Page number (1-indexed)
   * @param limit - Items per page
   * @param sortBy - Field to sort by
   * @param sortOrder - Sort order (asc/desc)
   * @returns Promise with paginated resumes
   */
  async getPaginatedResumes(
    page: number = 1,
    limit: number = 20,
    sortBy: string = "submitted_at",
    sortOrder: "asc" | "desc" = "desc",
  ): Promise<ResumeWithCandidate[]> {
    return this.getAdminResumes({
      page,
      limit,
      sort_by: sortBy,
      sort_order: sortOrder,
    });
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Validate file before upload
   *
   * @param file - File to validate
   * @throws Error if file doesn't meet requirements
   */
  private validateFile(file: File): void {
    if (file.size > ResumeModule.MAX_FILE_SIZE) {
      throw new Error(
        `File size must be less than ${this.formatFileSize(ResumeModule.MAX_FILE_SIZE)}`,
      );
    }

    if (!ResumeModule.ALLOWED_TYPES.includes(file.type)) {
      throw new Error("File must be a PDF");
    }
  }

  /**
   * Check if file type is supported
   *
   * @param file - File to check
   * @returns True if file type is supported
   */
  isSupportedFileType(file: File): boolean {
    return ResumeModule.ALLOWED_TYPES.includes(file.type);
  }

  /**
   * Format file size in human-readable format
   *
   * @param bytes - File size in bytes
   * @returns Formatted file size string
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  /**
   * Get file extension from filename
   *
   * @param filename - Filename to extract extension from
   * @returns File extension in lowercase
   */
  getFileExtension(filename: string): string {
    return filename.split(".").pop()?.toLowerCase() || "";
  }

  /**
   * Get maximum allowed file size
   *
   * @returns Maximum file size in bytes
   */
  getMaxFileSize(): number {
    return ResumeModule.MAX_FILE_SIZE;
  }

  /**
   * Get allowed file types
   *
   * @returns Array of allowed MIME types
   */
  getAllowedTypes(): string[] {
    return [...ResumeModule.ALLOWED_TYPES];
  }
}
